/**
 * Starts the picture behind the front and the loading scene (loaded by index.html ahead of the game's code).
 * It runs only while #loading or #front is on screen, and hides itself otherwise (so play costs nothing).
 *
 * One of the scenes (scenes/index.ts) is picked at random on each entry, never the one this browser saw last,
 * unless the viewer chose one in the lofi station's listen mode (front/lofi.ts), which is kept; only that scene's
 * module is loaded. The station's chip and listen mode load the first time the front shows. In a dev server a small picker shows any of them
 * (`?scene=<id>` too); it is never part of a build.
 */
import { Stage, type Focus } from './stage';
import { SCENES, type SceneId } from './scenes';
import { drawLogo } from './logo';
import { bandAt, frontHour, PAINTED } from './hourTone';

const canvas = document.getElementById('stage') as HTMLCanvasElement | null;
const host = document.getElementById('ink');
const LAST = 'c0front.last';

export let stage: Stage | null = null;
const wanted = (): boolean => {
  const l = document.getElementById('loading');
  const f = document.getElementById('front');
  return !!(l && !l.hidden && !l.classList.contains('done')) || !!(f && !f.hidden);
};
export function setFocus(f: Focus): void {
  stage?.setFocus(f);
}

/** Random, but not the same as the last one this viewer saw. */
export function pickScene(): SceneId {
  let last: string | null = null;
  try {
    const kept = localStorage.getItem('c0lofi.scene');
    const k = SCENES.find((x) => x.id === kept);
    if (k) return k.id;
    last = localStorage.getItem(LAST);
  } catch {
    // storage blocked: any scene may repeat
  }
  // A scene painted at about the world's hour (front/hourTone.ts), so the picture and the tone agree.
  const band = bandAt(frontHour());
  const fresh = SCENES.filter((s) => s.id !== last);
  const timed = fresh.filter((s) => PAINTED[s.id] === band);
  const pool = timed.length ? timed : fresh;
  const id = pool[Math.floor(Math.random() * pool.length)]!.id;
  try {
    localStorage.setItem(LAST, id);
  } catch {
    // ignore
  }
  return id;
}

if (canvas && host) {
  stage = new Stage(canvas);
  const s = stage;
  const sync = () => {
    const on = wanted();
    host.classList.toggle('on', on);
    const l = document.getElementById('loading');
    if (l && !l.hidden && !l.classList.contains('done')) s.setFocus('loading');
    if (on) s.start();
    else s.stop();
    const f = document.getElementById('front');
    if (f && !f.hidden && !lofi) lofi = import('./lofi').then((m) => m.mountLofi(s));
  };
  let lofi: Promise<void> | null = null;
  // Painted on the next frame, so the loading scene's own CSS shows first.
  requestAnimationFrame(() => {
    s.init();
    host.classList.add('ready');
    document.documentElement.classList.add('stage-ready');
    addEventListener('resize', () => s.resize());
    addEventListener('pointermove', (e) =>
      s.pointer((e.clientX / innerWidth - 0.5) * 2, (e.clientY / innerHeight - 0.5) * 2),
    );
    for (const id of ['loading', 'front']) {
      const el = document.getElementById(id);
      if (el)
        new MutationObserver(sync).observe(el, { attributes: true, attributeFilter: ['hidden', 'class'] });
    }
    const q = new URLSearchParams(location.search).get('scene');
    const first = (SCENES.find((x) => x.id === q)?.id ?? pickScene()) as SceneId;
    void s.setScene(first).then(sync);
    // Console access for dev (never a visible control): __frontScene('storm').
    if (import.meta.env.DEV)
      (window as unknown as Record<string, unknown>).__frontScene = (id: SceneId) => s.setScene(id);
    (window as unknown as Record<string, unknown>).__stage = s;
  });
  for (const c of document.querySelectorAll<HTMLCanvasElement>('canvas[data-logo]')) void drawLogo(c, true);
}
